import { KeyboardEvent, ReactNode, useRef } from 'react'
import { cn } from 'src/utils/style'
import { WithTooltip } from './tooltip'

export interface SegmentedOption<T extends string> {
  value: T
  label: ReactNode
  /** Hover/focus tooltip; also the option's accessible name. Use for icon-only labels. */
  tooltip?: string
  disabled?: boolean
  'data-testid'?: string
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[]
  value: T | null
  onChange: (value: T) => void
  /** Required: a radiogroup must be labelled. */
  'aria-label': string
  disabled?: boolean
  size?: 'sm' | 'md'
  /** Layout of the segments. `grid` with `columns` lays options out in a card grid. */
  layout?: 'row' | 'grid'
  columns?: number
  className?: string
  optionClassName?: string
  'data-testid'?: string
}

const sizeClasses: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
}

/**
 * Single-select segmented control with `radiogroup`/`radio` semantics and
 * roving-tabindex keyboard navigation (Arrow keys move+select, Home/End jump to
 * the first/last enabled option). This is the shared primitive for
 * mutually-exclusive option pickers — don't hand-roll a row of `<button>`s.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  disabled,
  size = 'md',
  layout = 'row',
  columns = 3,
  className,
  optionClassName,
  'data-testid': dataTestId,
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const enabledIndexes = options.reduce<number[]>((acc, opt, i) => {
    if (!disabled && !opt.disabled) acc.push(i)
    return acc
  }, [])

  const selectIndex = (index: number) => {
    const opt = options[index]
    if (!opt || disabled || opt.disabled) return
    refs.current[index]?.focus()
    onChange(opt.value)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (!enabledIndexes.length) return
    const pos = enabledIndexes.indexOf(currentIndex)
    let target: number | undefined
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = enabledIndexes[(pos + 1) % enabledIndexes.length]
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        target = enabledIndexes[(pos - 1 + enabledIndexes.length) % enabledIndexes.length]
        break
      case 'Home':
        target = enabledIndexes[0]
        break
      case 'End':
        target = enabledIndexes[enabledIndexes.length - 1]
        break
      default:
        return
    }
    e.preventDefault()
    if (target !== undefined) selectIndex(target)
  }

  // Roving tabindex: the selected option is the single tab stop; if nothing is
  // selected, the first enabled option takes the tab stop.
  const selectedIndex = options.findIndex((o) => o.value === value)
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : enabledIndexes[0]

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={cn(
        layout === 'grid'
          ? 'grid gap-2'
          : 'inline-flex items-center rounded-md border border-border',
        className,
      )}
      style={
        layout === 'grid'
          ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
          : undefined
      }
    >
      {options.map((opt, i) => {
        const isSelected = opt.value === value
        const isDisabled = disabled || opt.disabled
        return (
          <WithTooltip key={opt.value} label={opt.tooltip}>
            <button
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={opt.tooltip ?? (typeof opt.label === 'string' ? opt.label : undefined)}
              tabIndex={i === tabStopIndex ? 0 : -1}
              disabled={isDisabled}
              data-testid={opt['data-testid']}
              onClick={() => !isDisabled && onChange(opt.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={cn(
                'font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
                layout === 'grid'
                  ? 'rounded-lg border p-4 text-center'
                  : cn(sizeClasses[size], 'first:rounded-l-md last:rounded-r-md'),
                isSelected
                  ? layout === 'grid'
                    ? 'border-accent bg-accent text-white'
                    : 'bg-primary text-white'
                  : layout === 'grid'
                  ? 'border-border bg-surface text-text-primary hover:bg-surface-hover'
                  : 'bg-surface text-text-secondary hover:bg-surface-hover',
                isDisabled && 'cursor-not-allowed opacity-50',
                optionClassName,
              )}
            >
              {opt.label}
            </button>
          </WithTooltip>
        )
      })}
    </div>
  )
}

interface ToggleGroupProps<T extends string> {
  options: SegmentedOption<T>[]
  values: T[]
  onChange: (values: T[]) => void
  /** Required: a group of toggles must be labelled. */
  'aria-label': string
  disabled?: boolean
  layout?: 'row' | 'grid'
  columns?: number
  className?: string
  optionClassName?: string
  'data-testid'?: string
}

/**
 * Multi-select counterpart to {@link SegmentedControl}. Renders a labelled
 * `group` of `checkbox`-role toggles (each individually tab-focusable;
 * Space/Enter toggles via native button activation). Use this instead of a
 * hand-rolled grid of `<button>`s for "pick any number" option sets.
 */
export function ToggleGroup<T extends string>({
  options,
  values,
  onChange,
  'aria-label': ariaLabel,
  disabled,
  layout = 'row',
  columns = 3,
  className,
  optionClassName,
  'data-testid': dataTestId,
}: ToggleGroupProps<T>) {
  const toggle = (value: T) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={cn(layout === 'grid' ? 'grid gap-2' : 'inline-flex items-center gap-2', className)}
      style={
        layout === 'grid'
          ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
          : undefined
      }
    >
      {options.map((opt) => {
        const isChecked = values.includes(opt.value)
        const isDisabled = disabled || opt.disabled
        return (
          <button
            key={opt.value}
            type="button"
            role="checkbox"
            aria-checked={isChecked}
            aria-label={typeof opt.label === 'string' ? opt.label : undefined}
            disabled={isDisabled}
            data-testid={opt['data-testid']}
            onClick={() => !isDisabled && toggle(opt.value)}
            className={cn(
              'font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
              layout === 'grid'
                ? 'rounded-lg border p-4 text-center'
                : 'rounded-md border border-border px-3 py-1.5 text-sm',
              isChecked
                ? 'border-accent bg-accent text-white'
                : 'border-border bg-surface text-text-primary hover:bg-surface-hover',
              isDisabled && 'cursor-not-allowed opacity-50',
              optionClassName,
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
